import { useState } from 'react';
import { useAuthStore } from './store/authStore';
import { LoginScreen } from './features/auth/LoginScreen';
import { SubsidiaryPickerScreen } from './features/auth/SubsidiaryPickerScreen';
import { BranchPickerScreen } from './features/auth/BranchPickerScreen';
import { SetupScreen } from './features/auth/SetupScreen';
import { ServiceScreen } from './features/service/ServiceScreen';
import { KdsScreen } from './features/kds/KdsScreen';

/**
 * App.tsx — Phase 7.5.
 *
 * Routes between auth states using the discriminated state machine in
 * authStore. The ordering matches the user's progression:
 *
 *   unauthenticated     → LoginScreen
 *   logging-in          → LoginScreen (with the form's own busy indicator)
 *   pending             → SubsidiaryPickerScreen
 *   choosing-subsidiary → SubsidiaryPickerScreen (busy=true)
 *   subsidiary-chosen   → BranchPickerScreen
 *   choosing-branch     → BranchPickerScreen (busy=true)
 *   session             → ServiceScreen
 *
 * No router library — the flow is strictly linear, conditional rendering
 * is enough. If we add deep linking later we'll revisit.
 */
export function App(): JSX.Element {
  const state = useAuthStore((s) => s.state);

  switch (state.kind) {
    case 'unauthenticated':
      return <LoginScreen banner={state.banner} />;

    case 'logging-in':
      // Same screen as unauthenticated but the form is in submitting mode.
      // The login store's `state.kind === 'logging-in'` is what the form
      // reads to know to render its busy state.
      return <LoginScreen banner={null} />;

    case 'pending':
    case 'choosing-subsidiary':
      return (
        <SubsidiaryPickerScreen
          user={state.user}
          subsidiaries={state.subsidiaries}
          busy={state.kind === 'choosing-subsidiary'}
        />
      );

    case 'subsidiary-chosen':
    case 'choosing-branch':
      return (
        <BranchPickerScreen
          user={state.user}
          subsidiary={state.subsidiary}
          branches={state.branches}
          busy={state.kind === 'choosing-branch'}
        />
      );

    case 'setup':
      return <SetupScreen />;

    case 'session':
      return <SignedIn />;
  }
}

/**
 * Two surfaces behind one login.
 *
 * The Kitchen Display is the default — it is the screen that runs all shift on
 * a wall. The legacy Service Manager grid is kept because it is what managers
 * at existing tenants know, it answers a different question (every order for a
 * date range, across branches, with the ORDER DATE / DELIVERY DATE toggle), and
 * removing it would take away a working tool during the KDS rollout.
 *
 * The choice is remembered per device: a tablet bolted over the pass and a
 * manager's laptop want different defaults, and neither should have to re-pick
 * after a reboot.
 */
const SURFACE_KEY = 'matix_kds_surface';

function SignedIn(): JSX.Element {
  const [surface, setSurface] = useState<'kds' | 'service'>(() =>
    localStorage.getItem(SURFACE_KEY) === 'service' ? 'service' : 'kds',
  );

  const choose = (next: 'kds' | 'service'): void => {
    try {
      localStorage.setItem(SURFACE_KEY, next);
    } catch {
      // Private mode. The choice still holds for this session.
    }
    setSurface(next);
  };

  if (surface === 'service') {
    return <ServiceScreen onOpenKds={() => choose('kds')} />;
  }
  return <KdsScreen onOpenServiceManager={() => choose('service')} />;
}
